import { useNavigation, useRoute } from '@react-navigation/native'
import { useTheme } from 'contexts'
import React, { useEffect, useMemo } from 'react'
import { Dimensions } from 'react-native'
import { TabView as RNTabView, Route, TabViewProps } from 'react-native-tab-view'

import { Box } from 'widgets/Box'

import { TabBar } from './TabBar'

interface Props {
  actions?: React.ReactNode
  children: React.ReactNode | React.ReactNode[]
  header?: React.ReactNode
  hideActionsTabs?: string[]
  initialTabIndex?: number
  selectedTabIndex?: number
  tabs?: string[]
  testID?: string
  style?: TabViewProps<Route>['style']
  onTabChange?: (index: number) => void
}

export const TabView = ({
  actions,
  children,
  header,
  hideActionsTabs = [],
  initialTabIndex = 0,
  selectedTabIndex = 0,
  tabs = [],
  testID,
  onTabChange,
}: Props) => {
  const theme = useTheme()
  const navigation = useNavigation()
  const route = useRoute()

  const routeParams = route?.params as any
  const routeParamsTabName = routeParams?.tab
  const routeParamsTab = routeParamsTabName
    ? tabs.findIndex((tab) => tab.toLowerCase() === routeParamsTabName.toLowerCase())
    : 0
  const [tabIndex, setTabIndex] = React.useState(routeParamsTab || initialTabIndex)

  useEffect(() => {
    handleSetTabIndex(tabIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabIndex])

  useEffect(() => {
    if (routeParamsTabName !== tabs[tabIndex]) {
      setTabIndex(routeParamsTab)
    }
  }, [routeParamsTabName, routeParamsTab, tabs, tabIndex])

  useEffect(() => {
    onTabChange && onTabChange(tabIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex])

  // This allows us to pass one or many children
  const arrayChildren = useMemo(
    () => (Array.isArray(children) ? children.filter((child) => child) : [children]),
    [children],
  )

  const tabRoutes = useMemo(() => {
    return arrayChildren && arrayChildren.length
      ? arrayChildren.map((child, index) => ({
          key: tabs && tabs[index] && tabs[index].toLowerCase(),
          title: tabs[index],
          testID: tabs[index],
          Component: child,
        }))
      : []
  }, [arrayChildren, tabs])

  const handleSetTabIndex = (index: number) => {
    const tabName = tabs[index]
    if (tabName) {
      const param = { tab: tabName.toLowerCase() } as never
      navigation.setParams(param)
    }
  }

  return (
    <Box flex={1} testID={testID}>
      {header && <Box>{header}</Box>}

      <RNTabView
        swipeEnabled={false}
        onIndexChange={handleSetTabIndex}
        navigationState={{ index: tabIndex, routes: tabRoutes }}
        renderScene={({ route: tabRoute }) => {
          return tabRoute.Component
        }}
        renderTabBar={(props) => (
          <TabBar
            actions={actions}
            hideActions={hideActionsTabs.includes(tabs[tabIndex])}
            routeCount={tabRoutes?.length}
            testID={testID}
            {...props}
          />
        )}
        initialLayout={{
          width: Math.min(theme.maxWidth, Dimensions.get('window').width),
        }}
      />
    </Box>
  )
}
